import React, { useEffect, useMemo, useState } from "react"
import api from "../../utils/api"
import { formatDateLong, formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { getGroupTheme } from "../../utils/groupTheme"
import { CheckCircle2, Lock, Unlock, CreditCard, Users, Plus, Trash2, ShieldCheck, AlertCircle, Clock } from "lucide-react"

const FAMILY_MEMBER_STATUS_OPTIONS = [
  { value: "deceased", label: "Deceased" },
  { value: "na", label: "N/A" },
  { value: "alive", label: "Alive" },
]

const normalizeFamilyMemberStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized === "active") return "alive"
  if (normalized === "deceased" || normalized === "na") return normalized
  return "alive"
}

const normalizeParentType = (value, fallback = "") => {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized === "father" || normalized === "mother") return normalized
  const normalizedFallback = String(fallback || "").trim().toLowerCase()
  return normalizedFallback === "father" || normalizedFallback === "mother"
    ? normalizedFallback
    : ""
}

const resolveParentTypeFromSpouseParentSlot = (slot) => {
  const normalizedSlot = Number(slot || 0)
  if (normalizedSlot === 1) return "father"
  if (normalizedSlot === 2) return "mother"
  return ""
}

const getParentDisplayLabel = (parentType, fallback = "Parent") => {
  const normalizedParentType = normalizeParentType(parentType)
  if (normalizedParentType === "father") return "Father"
  if (normalizedParentType === "mother") return "Mother"
  return fallback
}

const createFamilyRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fullName: "",
  dateOfBirth: "",
  status: "alive",
})

const createSpouseRow = () => ({
  ...createFamilyRow(),
  slotKey: `sp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  parentOneName: "",
  parentOneDob: "",
  parentOneStatus: "alive",
  parentTwoName: "",
  parentTwoDob: "",
  parentTwoStatus: "alive",
})

const createDefaultParentRows = () => [
  { ...createFamilyRow(), id: "parent-father", parentType: "father" },
  { ...createFamilyRow(), id: "parent-mother", parentType: "mother" },
]

const buildMandatoryParentRows = (rows = []) => {
  const source = Array.isArray(rows) ? rows : []
  const defaults = createDefaultParentRows()
  return defaults.map((fallbackRow, index) => {
    const sourceRowByType = source.find(
      (row) => normalizeParentType(row?.parentType) === normalizeParentType(fallbackRow.parentType)
    ) || null
    const sourceRow = sourceRowByType || source[index] || null
    return {
      ...fallbackRow,
      fullName: sourceRow?.fullName || "",
      dateOfBirth: sourceRow?.dateOfBirth || "",
      status: normalizeFamilyMemberStatus(sourceRow?.status),
      parentType: normalizeParentType(sourceRow?.parentType, fallbackRow.parentType),
    }
  })
}

const toDateInputValue = (value) => {
  if (!value) return ""
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ""
  return dt.toISOString().slice(0, 10)
}

const toFamilyPayload = ({ rows = [], relationType }) =>
  rows.map((row) => ({
    relationType,
    fullName: String(row?.fullName || "").trim(),
    dateOfBirth: String(row?.dateOfBirth || ""),
    status: normalizeFamilyMemberStatus(row?.status),
    parentType: normalizeParentType(row?.parentType),
    spouseSlotKey: String(row?.slotKey || row?.spouseSlotKey || "").trim(),
  }))

const isStatusAlive = (value) => normalizeFamilyMemberStatus(value) === "alive"

const parseSafeDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const normalizeProbationDuration = (probation) => {
  const value = Number(probation?.value || 0)
  const unit = String(probation?.unit || "").trim().toLowerCase()
  if (!Number.isInteger(value) || value <= 0) return null
  if (!["days", "months"].includes(unit)) return null
  return { value, unit }
}

const shiftDateByProbationDuration = (date, probation, direction = 1) => {
  if (!date || Number.isNaN(date.getTime()) || !probation) return null
  const next = new Date(date)
  const delta = Number(direction || 1) * Number(probation.value || 0)
  if (probation.unit === "days") next.setDate(next.getDate() + delta)
  else next.setMonth(next.getMonth() + delta)
  return next
}

function GroupWelfareOnboardingModal({ group, onGroupUpdated }) {
  const theme = getGroupTheme(group)
  const [isLoading, setIsLoading] = useState(false)
  const [isSavingStep2, setIsSavingStep2] = useState(false)
  const [isPayingStep1, setIsPayingStep1] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [onboarding, setOnboarding] = useState(null)
  const [coverage, setCoverage] = useState({})
  const [parents, setParents] = useState(() => createDefaultParentRows())
  const [spouses, setSpouses] = useState([])
  const [children, setChildren] = useState([])
  const [siblings, setSiblings] = useState([])
  const myMemberEntry = useMemo(
    () =>
      Array.isArray(group?.members)
        ? group.members.find((member) => String(member?.id || "") === String(group?.myMemberId || "")) || null
        : null,
    [group?.members, group?.myMemberId]
  )
  const welfareProbationDisplay = (() => {
    const member = myMemberEntry
    if (!member) return null
    const now = new Date()
    const probationDuration = normalizeProbationDuration(group?.welfare?.probation || null)
    const probationReason = String(member?.welfareProbationReason || "").trim().toLowerCase()

    const latePaymentEnd = parseSafeDate(member?.welfareProbationEndsAt)
    const latePaymentStartRaw = parseSafeDate(member?.welfareProbationStartedAt)
    const latePaymentStart =
      latePaymentStartRaw ||
      (latePaymentEnd && probationDuration
        ? shiftDateByProbationDuration(latePaymentEnd, probationDuration, -1)
        : null)

    const registrationEligibleAt = parseSafeDate(member?.registrationEligibleAt)
    const registrationInvoicedAt = parseSafeDate(member?.registrationInvoicedAt)
    const isEnrollmentProbation =
      !!registrationEligibleAt &&
      registrationEligibleAt.getTime() > now.getTime() &&
      !registrationInvoicedAt
    if (isEnrollmentProbation) {
      const enrollmentEnd = parseSafeDate(group?.welfare?.enrollment?.endDate)
      const startAt =
        enrollmentEnd ||
        (registrationEligibleAt && probationDuration
          ? shiftDateByProbationDuration(registrationEligibleAt, probationDuration, -1)
          : null)
      return {
        scenarioLabel: "Enrollment probation",
        startAt,
        endAt: registrationEligibleAt,
      }
    }

    const isProbation =
      String(member?.welfareStatus || "").toLowerCase() === "probation" &&
      (!latePaymentEnd || latePaymentEnd.getTime() > now.getTime())
    if (!isProbation) return null

    const scenarioLabel =
      probationReason === "late_payment"
        ? "Late-payment probation"
        : probationReason === "join"
          ? "Joining probation"
          : "Probation"

    return {
      scenarioLabel,
      startAt: latePaymentStart,
      endAt:
        latePaymentEnd ||
        (latePaymentStart && probationDuration
          ? shiftDateByProbationDuration(latePaymentStart, probationDuration, 1)
          : null),
    }
  })()

  const loadOnboarding = async () => {
    if (!group?.id) return
    setIsLoading(true)
    setError("")
    try {
      const res = await api.get(`/groups/${group.id}/welfare/onboarding`)
      const nextOnboarding = res?.data?.onboarding || null
      const nextCoverage = res?.data?.coverage || {}
      const familyMembers = Array.isArray(nextOnboarding?.step2?.familyMembers)
        ? nextOnboarding.step2.familyMembers
        : []

      const parentRows = familyMembers
        .filter((row) => String(row?.relationType || "") === "parent")
        .map((row) => ({
          id: String(row?.id || `${Math.random()}`),
          fullName: row?.fullName || "",
          dateOfBirth: toDateInputValue(row?.dateOfBirth),
          status: normalizeFamilyMemberStatus(row?.status),
          parentType: normalizeParentType(row?.parentType),
        }))
      const childRows = familyMembers
        .filter((row) => String(row?.relationType || "") === "child")
        .map((row) => ({
          id: String(row?.id || `${Math.random()}`),
          fullName: row?.fullName || "",
          dateOfBirth: toDateInputValue(row?.dateOfBirth),
          status: normalizeFamilyMemberStatus(row?.status),
        }))
      const siblingRows = familyMembers
        .filter((row) => String(row?.relationType || "") === "sibling")
        .map((row) => ({
          id: String(row?.id || `${Math.random()}`),
          fullName: row?.fullName || "",
          dateOfBirth: toDateInputValue(row?.dateOfBirth),
          status: normalizeFamilyMemberStatus(row?.status),
        }))

      const spouseRowsRaw = familyMembers.filter(
        (row) => String(row?.relationType || "") === "spouse"
      )
      const spouseParentRowsRaw = familyMembers.filter(
        (row) => String(row?.relationType || "") === "spouse_parent"
      )
      const spouseRows = spouseRowsRaw.map((spouse, index) => {
        const slotKey = String(spouse?.spouseSlotKey || `sp-existing-${index + 1}`)
        const firstParent = spouseParentRowsRaw.find(
          (row) =>
            String(row?.spouseSlotKey || "") === slotKey &&
            (Number(row?.spouseParentSlot || 0) === 1 ||
              normalizeParentType(row?.parentType, resolveParentTypeFromSpouseParentSlot(row?.spouseParentSlot)) === "father")
        )
        const secondParent = spouseParentRowsRaw.find(
          (row) =>
            String(row?.spouseSlotKey || "") === slotKey &&
            (Number(row?.spouseParentSlot || 0) === 2 ||
              normalizeParentType(row?.parentType, resolveParentTypeFromSpouseParentSlot(row?.spouseParentSlot)) === "mother")
        )
        return {
          id: String(spouse?.id || `${Math.random()}`),
          slotKey,
          fullName: spouse?.fullName || "",
          dateOfBirth: toDateInputValue(spouse?.dateOfBirth),
          status: normalizeFamilyMemberStatus(spouse?.status),
          parentOneName: firstParent?.fullName || "",
          parentOneDob: toDateInputValue(firstParent?.dateOfBirth),
          parentOneStatus: normalizeFamilyMemberStatus(firstParent?.status),
          parentTwoName: secondParent?.fullName || "",
          parentTwoDob: toDateInputValue(secondParent?.dateOfBirth),
          parentTwoStatus: normalizeFamilyMemberStatus(secondParent?.status),
        }
      })

      setOnboarding(nextOnboarding)
      setCoverage(nextCoverage)
      setParents(buildMandatoryParentRows(parentRows))
      setSpouses(spouseRows.length ? spouseRows : [])
      setChildren(childRows.length ? childRows : [])
      setSiblings(siblingRows.length ? siblingRows : [])
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load Welfare onboarding."))
      setOnboarding(null)
      setParents(createDefaultParentRows())
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadOnboarding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id])

  const registrationInvoice = onboarding?.step1?.registrationInvoice || null
  const step1Complete = !!onboarding?.step1?.isCompleted
  const step2Complete = !!onboarding?.step2?.isCompleted
  const onboardingComplete = !!onboarding?.isCompleted
  const welfareMemberIdentifier = String(onboarding?.welfareMemberIdentifier || "")
    .trim()
    .replace(/^#/i, "M")

  const relationFlags = useMemo(
    () => ({
      parent: coverage?.parent !== false,
      spouse: coverage?.spouse !== false,
      spouseParent: coverage?.spouseParent !== false,
      child: coverage?.child !== false,
      sibling: coverage?.sibling !== false,
    }),
    [coverage]
  )

  const handlePayRegistration = async () => {
    if (!group?.id || !registrationInvoice?.id) return
    setIsPayingStep1(true)
    setError("")
    setSuccess("")
    try {
      const amount = Number(registrationInvoice?.balanceAmount || 0)
      const res = await api.post(`/groups/${group.id}/invoices/${registrationInvoice.id}/pay`, { amount })
      if (res?.data?.group?.id) {
        onGroupUpdated?.(res.data.group)
      }
      setSuccess("Registration fee paid. Step 2 is now unlocked.")
      await loadOnboarding()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to pay registration invoice."))
    } finally {
      setIsPayingStep1(false)
    }
  }

  const upsertRow = (setter, rowId, key, value) => {
    setter((prev) =>
      prev.map((row) => (String(row.id) === String(rowId) ? { ...row, [key]: value } : row))
    )
  }

  const removeRow = (setter, rowId) => {
    setter((prev) => prev.filter((row) => String(row.id) !== String(rowId)))
  }

  const validateRows = (rows, label) => {
    for (const row of rows) {
      const fullName = String(row?.fullName || "").trim()
      const dateOfBirth = String(row?.dateOfBirth || "").trim()
      if (!fullName && !dateOfBirth) continue
      if (!fullName || !dateOfBirth) {
        return `${label}: each entry needs both full name and date of birth.`
      }
    }
    return ""
  }

  const validateMandatoryRows = (rows, label) => {
    for (const row of rows) {
      const status = normalizeFamilyMemberStatus(row?.status)
      if (status !== "alive") continue
      const fullName = String(row?.fullName || "").trim()
      const dateOfBirth = String(row?.dateOfBirth || "").trim()
      if (!fullName || !dateOfBirth) {
        return `${label}: Alive entries need both full name and date of birth.`
      }
    }
    return ""
  }

  const handleSaveStep2 = async () => {
    if (!group?.id) return
    setError("")
    setSuccess("")

    const mandatoryParents = relationFlags.parent ? buildMandatoryParentRows(parents) : []
    if (relationFlags.parent && mandatoryParents.length !== 2) {
      setError("Parents: exactly 2 entries are required (Father and Mother).")
      return
    }

    const validationMessage =
      (relationFlags.parent ? validateMandatoryRows(mandatoryParents, "Parents") : "") ||
      (relationFlags.spouse ? validateRows(spouses, "Spouse") : "") ||
      (relationFlags.child ? validateRows(children, "Children") : "") ||
      (relationFlags.sibling ? validateRows(siblings, "Siblings") : "")
    if (validationMessage) {
      setError(validationMessage)
      return
    }

    const totalMembers =
      (relationFlags.parent ? mandatoryParents.length : 0) +
      spouses.length +
      children.length +
      siblings.length
    if (totalMembers === 0) {
      setError("You must add at least 1 family member to complete the joining process.")
      return
    }

    if (relationFlags.spouse && relationFlags.spouseParent) {
      for (const spouse of spouses) {
        const hasSpouse = String(spouse?.fullName || "").trim() && String(spouse?.dateOfBirth || "").trim()
        if (!hasSpouse) continue
        const hasFirstParent =
          !isStatusAlive(spouse?.parentOneStatus) ||
          (String(spouse?.parentOneName || "").trim() && String(spouse?.parentOneDob || "").trim())
        const hasSecondParent =
          !isStatusAlive(spouse?.parentTwoStatus) ||
          (String(spouse?.parentTwoName || "").trim() && String(spouse?.parentTwoDob || "").trim())
        if (!hasFirstParent || !hasSecondParent) {
          setError("Each spouse must include both Father and Mother statuses. If status is Alive, name + date of birth are required.")
          return
        }
      }
    }

    const payloadRows = [
      ...(relationFlags.parent ? toFamilyPayload({ rows: mandatoryParents, relationType: "parent" }) : []),
      ...(relationFlags.spouse ? toFamilyPayload({ rows: spouses, relationType: "spouse" }) : []),
      ...(relationFlags.child ? toFamilyPayload({ rows: children, relationType: "child" }) : []),
      ...(relationFlags.sibling ? toFamilyPayload({ rows: siblings, relationType: "sibling" }) : []),
    ].filter((row) => {
      const status = normalizeFamilyMemberStatus(row?.status)
      if (status !== "alive") return true
      return String(row?.fullName || "").trim() && String(row?.dateOfBirth || "").trim()
    })

    if (relationFlags.spouse && relationFlags.spouseParent) {
      for (const spouse of spouses) {
        const hasSpouse = String(spouse?.fullName || "").trim() && String(spouse?.dateOfBirth || "").trim()
        if (!hasSpouse) continue
        const slotKey = String(spouse?.slotKey || createSpouseRow().slotKey)
        payloadRows.push({
          relationType: "spouse_parent",
          fullName: String(spouse?.parentOneName || "").trim(),
          dateOfBirth: String(spouse?.parentOneDob || "").trim(),
          status: normalizeFamilyMemberStatus(spouse?.parentOneStatus),
          parentType: "father",
          spouseSlotKey: slotKey,
          spouseParentSlot: 1,
        })
        payloadRows.push({
          relationType: "spouse_parent",
          fullName: String(spouse?.parentTwoName || "").trim(),
          dateOfBirth: String(spouse?.parentTwoDob || "").trim(),
          status: normalizeFamilyMemberStatus(spouse?.parentTwoStatus),
          parentType: "mother",
          spouseSlotKey: slotKey,
          spouseParentSlot: 2,
        })
      }
    }

    setIsSavingStep2(true)
    try {
      const res = await api.put(`/groups/${group.id}/welfare/onboarding/family-members`, {
        familyMembers: payloadRows,
      })
      if (res?.data?.group?.id) {
        onGroupUpdated?.(res.data.group)
      }
      setSuccess("Family members saved. Onboarding complete.")
      await loadOnboarding()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save family members."))
    } finally {
      setIsSavingStep2(false)
    }
  }

  const renderSimpleRows = (label, rows, setter, maxRows = null) => (
    <div className="space-y-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className={`w-4 h-4 ${theme.text}`} />
          <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
        </div>
        {(!maxRows || rows.length < maxRows) && (
          <button
            type="button"
            onClick={() => setter((prev) => [...prev, createFamilyRow()])}
            className={`flex items-center gap-1 text-[11px] font-medium ${theme.text} hover:opacity-80 ${theme.bgMuted50} px-2.5 py-1 rounded-md transition-colors`}
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>
      <div className="space-y-3">
        {rows.length === 0 ? (
          <div className="py-4 px-4 bg-gray-50/50 border border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-center">
            <p className="text-[11px] text-gray-500 mb-2">No {label.toLowerCase()} added yet.</p>
            <button
              type="button"
              onClick={() => setter((prev) => [...prev, createFamilyRow()])}
              className={`text-[11px] font-medium ${theme.text} hover:opacity-80 bg-white border border-gray-200 px-3 py-1.5 rounded-md shadow-sm transition-colors`}
            >
              Add {label.endsWith('s') ? label.slice(0, -1) : label}
            </button>
          </div>
        ) : (
          rows.map((row, index) => (
            <div key={row.id} className={`relative grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg bg-gray-50/50 border border-gray-100 group hover:${theme.border.split(' ')[0]} transition-colors`}>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Full Name</label>
                <input
                  type="text"
                  value={row.fullName}
                  onChange={(e) => upsertRow(setter, row.id, "fullName", e.target.value)}
                  placeholder="Enter full name"
                  className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Date of Birth</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={row.dateOfBirth}
                    onChange={(e) => upsertRow(setter, row.id, "dateOfBirth", e.target.value)}
                    className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(setter, row.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )

  const renderMandatoryParents = () => {
    const rows = buildMandatoryParentRows(parents)
    return (
      <div className="space-y-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Users className={`w-4 h-4 ${theme.text}`} />
          <h4 className="text-sm font-semibold text-gray-900">Parents</h4>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Required: Father + Mother</span>
        </div>
        <div className="space-y-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className={`grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-lg bg-gray-50/50 border border-gray-100 group hover:${theme.border.split(' ')[0]} transition-colors`}
            >
              <div className="space-y-1.5">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Status</label>
                <select
                  value={normalizeFamilyMemberStatus(row?.status)}
                  onChange={(e) => {
                    const nextStatus = normalizeFamilyMemberStatus(e.target.value)
                    upsertRow(setParents, row.id, "status", nextStatus)
                    if (nextStatus !== "alive") {
                      upsertRow(setParents, row.id, "fullName", "")
                      upsertRow(setParents, row.id, "dateOfBirth", "")
                    }
                  }}
                  className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                >
                  {FAMILY_MEMBER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  {getParentDisplayLabel(row?.parentType)} Full Name
                </label>
                <input
                  type="text"
                  value={row.fullName}
                  disabled={!isStatusAlive(row?.status)}
                  onChange={(e) => upsertRow(setParents, row.id, "fullName", e.target.value)}
                  placeholder={isStatusAlive(row?.status) ? "Enter full name" : "Not required"}
                  className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Date of Birth</label>
                <input
                  type="date"
                  value={row.dateOfBirth}
                  disabled={!isStatusAlive(row?.status)}
                  onChange={(e) => upsertRow(setParents, row.id, "dateOfBirth", e.target.value)}
                  className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!group?.id) return null

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className={`bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden`}>
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-5 rounded-full blur-2xl"></div>
        <div className="relative z-10 flex items-start gap-4">
          <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
            <ShieldCheck className="w-8 h-8 text-slate-300" />
          </div>
          <div>
            <h3 className="text-xl font-bold tracking-tight">Welfare Onboarding</h3>
            <p className="text-slate-300 text-sm mt-1.5 max-w-lg leading-relaxed">
              Complete the setup process below to unlock full benefits and protections for you and your eligible family members.
            </p>
          </div>
        </div>
      </div>

      {welfareProbationDisplay && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800">
          <p className="font-semibold">{welfareProbationDisplay.scenarioLabel}</p>
          <p className="mt-1">
            Start date:{" "}
            <span className="font-semibold">
              {welfareProbationDisplay.startAt
                ? formatDateLong(welfareProbationDisplay.startAt)
                : "-"}
            </span>
          </p>
          <p>
            End date:{" "}
            <span className="font-semibold">
              {welfareProbationDisplay.endAt
                ? formatDateLong(welfareProbationDisplay.endAt)
                : "-"}
            </span>
          </p>
        </div>
      )}

      {!!error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 animate-in fade-in slide-in-from-top-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 font-medium">{error}</p>
        </div>
      )}
      {!!success && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 animate-in fade-in slide-in-from-top-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800 font-medium">{success}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.replace('border-', 'border-b-')}`}></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Step 1 */}
          <div className={`relative bg-white rounded-2xl border ${step1Complete ? 'border-emerald-200' : 'border-gray-200 shadow-sm'} overflow-hidden transition-all duration-300`}>
            {step1Complete && <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>}
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${step1Complete ? 'bg-emerald-100 text-emerald-700' : `${theme.bg} ${theme.text}`}`}>
                    1
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Registration Fee</h4>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${step1Complete ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  {step1Complete ? (
                    <><CheckCircle2 className="w-3.5 h-3.5" /> Completed</>
                  ) : (
                    <><Clock className="w-3.5 h-3.5" /> Pending Payment</>
                  )}
                </div>
              </div>

              <div className="mt-4 sm:mt-0 sm:ml-11">
                {registrationInvoice ? (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Amount Due</p>
                        <p className="text-lg font-bold text-gray-900">{formatMoney(registrationInvoice.amount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Balance</p>
                        <p className={`text-lg font-bold ${registrationInvoice.balanceAmount > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {formatMoney(registrationInvoice.balanceAmount || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Due Date</p>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {registrationInvoice.dueDate ? formatDateLong(registrationInvoice.dueDate) : "Immediate"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mb-4 text-sm text-gray-600">
                    Registration invoice is currently being prepared.
                  </div>
                )}

                {!step1Complete && (
                  <button
                    type="button"
                    onClick={handlePayRegistration}
                    disabled={!registrationInvoice?.id || isPayingStep1 || registrationInvoice.balanceAmount <= 0}
                    className={`flex items-center gap-2 ${theme.button} px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm`}
                  >
                    <CreditCard className="w-4 h-4" />
                    {isPayingStep1 ? "Processing Payment..." : "Pay Registration Fee"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className={`relative bg-white rounded-2xl border ${step2Complete ? 'border-emerald-200' : 'border-gray-200 shadow-sm'} overflow-hidden transition-all duration-300 ${!step1Complete ? 'opacity-75' : ''}`}>
            {step2Complete && <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>}
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${step2Complete ? 'bg-emerald-100 text-emerald-700' : (!step1Complete ? 'bg-gray-100 text-gray-500' : `${theme.bg} ${theme.text}`)}`}>
                    2
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Family Members</h4>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${step2Complete ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : (!step1Complete ? 'bg-gray-100 text-gray-600 border border-gray-200' : 'bg-amber-50 text-amber-700 border border-amber-200')}`}>
                  {step2Complete ? (
                    <><CheckCircle2 className="w-3.5 h-3.5" /> Completed</>
                  ) : !step1Complete ? (
                    <><Lock className="w-3.5 h-3.5" /> Locked</>
                  ) : (
                    <><Unlock className="w-3.5 h-3.5" /> Unlocked</>
                  )}
                </div>
              </div>

              <div className="mt-4 sm:mt-0 sm:ml-11">
                {!step1Complete ? (
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col items-center justify-center text-center space-y-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <Lock className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Action Required</p>
                      <p className="text-sm text-gray-500 mt-1">Please complete Step 1 (Registration Fee) to unlock family member enrollment.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2">
                    {relationFlags.parent && renderMandatoryParents()}
                    
                    {relationFlags.spouse && (
                      <div className="space-y-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className={`w-4 h-4 ${theme.text}`} />
                            <h4 className="text-sm font-semibold text-gray-900">Spouse Details</h4>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSpouses((prev) => [...prev, createSpouseRow()])}
                            className={`flex items-center gap-1 text-[11px] font-medium ${theme.text} hover:opacity-80 ${theme.bgMuted50} px-2.5 py-1 rounded-md transition-colors`}
                          >
                            <Plus className="w-3 h-3" />
                            Add Spouse
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                          {spouses.length === 0 ? (
                            <div className="py-4 px-4 bg-gray-50/50 border border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-center">
                              <p className="text-[11px] text-gray-500 mb-2">No spouse added yet.</p>
                              <button
                                type="button"
                                onClick={() => setSpouses((prev) => [...prev, createSpouseRow()])}
                                className={`text-[11px] font-medium ${theme.text} hover:opacity-80 bg-white border border-gray-200 px-3 py-1.5 rounded-md shadow-sm transition-colors`}
                              >
                                Add Spouse
                              </button>
                            </div>
                          ) : (
                            spouses.map((row, index) => (
                              <div key={row.id} className={`relative p-4 rounded-xl bg-gray-50/50 border border-gray-200 group hover:${theme.border.split(' ')[0]} transition-colors`}>
                                <div className="absolute top-3 right-3">
                                  <button
                                    type="button"
                                    onClick={() => removeRow(setSpouses, row.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                    title="Remove Spouse"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 pr-8">
                                  <div className="space-y-1.5">
                                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Spouse Full Name</label>
                                    <input
                                      type="text"
                                      value={row.fullName}
                                      onChange={(e) => upsertRow(setSpouses, row.id, "fullName", e.target.value)}
                                      placeholder="Enter full name"
                                      className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Date of Birth</label>
                                    <input
                                      type="date"
                                      value={row.dateOfBirth}
                                      onChange={(e) => upsertRow(setSpouses, row.id, "dateOfBirth", e.target.value)}
                                      className={`w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 ${theme.ring} transition-shadow`}
                                    />
                                  </div>
                                </div>

                                {relationFlags.spouseParent && (
                                  <div className="mt-4 pt-4 border-t border-gray-200/60">
                                    <h5 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                                      <span className={`w-1 h-3 ${theme.bg.replace('/10', '/60')} rounded-full`}></span>
                                      Spouse's Parents
                                    </h5>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <div className="space-y-3 p-3 rounded-lg bg-white border border-gray-100 shadow-sm">
                                        <p className={`text-[11px] font-semibold ${theme.text} ${theme.bgMuted50} px-2 py-0.5 rounded inline-block`}>Father</p>
                                        <div>
                                          <select
                                            value={normalizeFamilyMemberStatus(row?.parentOneStatus)}
                                            onChange={(e) => {
                                              const nextStatus = normalizeFamilyMemberStatus(e.target.value)
                                              upsertRow(setSpouses, row.id, "parentOneStatus", nextStatus)
                                              if (!isStatusAlive(nextStatus)) {
                                                upsertRow(setSpouses, row.id, "parentOneName", "")
                                                upsertRow(setSpouses, row.id, "parentOneDob", "")
                                              }
                                            }}
                                            className={`w-full px-3 py-1.5 mb-2 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          >
                                            {FAMILY_MEMBER_STATUS_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                          <input
                                            type="text"
                                            value={row.parentOneName}
                                            disabled={!isStatusAlive(row?.parentOneStatus)}
                                            onChange={(e) => upsertRow(setSpouses, row.id, "parentOneName", e.target.value)}
                                            placeholder={isStatusAlive(row?.parentOneStatus) ? "Full name" : "Not required"}
                                            className={`w-full px-3 py-1.5 mb-2 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          />
                                          <input
                                            type="date"
                                            value={row.parentOneDob}
                                            disabled={!isStatusAlive(row?.parentOneStatus)}
                                            onChange={(e) => upsertRow(setSpouses, row.id, "parentOneDob", e.target.value)}
                                            className={`w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-3 p-3 rounded-lg bg-white border border-gray-100 shadow-sm">
                                        <p className={`text-[11px] font-semibold ${theme.text} ${theme.bgMuted50} px-2 py-0.5 rounded inline-block`}>Mother</p>
                                        <div>
                                          <select
                                            value={normalizeFamilyMemberStatus(row?.parentTwoStatus)}
                                            onChange={(e) => {
                                              const nextStatus = normalizeFamilyMemberStatus(e.target.value)
                                              upsertRow(setSpouses, row.id, "parentTwoStatus", nextStatus)
                                              if (!isStatusAlive(nextStatus)) {
                                                upsertRow(setSpouses, row.id, "parentTwoName", "")
                                                upsertRow(setSpouses, row.id, "parentTwoDob", "")
                                              }
                                            }}
                                            className={`w-full px-3 py-1.5 mb-2 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          >
                                            {FAMILY_MEMBER_STATUS_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                          <input
                                            type="text"
                                            value={row.parentTwoName}
                                            disabled={!isStatusAlive(row?.parentTwoStatus)}
                                            onChange={(e) => upsertRow(setSpouses, row.id, "parentTwoName", e.target.value)}
                                            placeholder={isStatusAlive(row?.parentTwoStatus) ? "Full name" : "Not required"}
                                            className={`w-full px-3 py-1.5 mb-2 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          />
                                          <input
                                            type="date"
                                            value={row.parentTwoDob}
                                            disabled={!isStatusAlive(row?.parentTwoStatus)}
                                            onChange={(e) => upsertRow(setSpouses, row.id, "parentTwoDob", e.target.value)}
                                            className={`w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:bg-white focus:ring-2 ${theme.ring}`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                    
                    {relationFlags.child && renderSimpleRows("Children", children, setChildren)}
                    {relationFlags.sibling && renderSimpleRows("Siblings", siblings, setSiblings)}

                    <div className="pt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveStep2}
                        disabled={isSavingStep2}
                        className="flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      >
                        {isSavingStep2 ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Saving...</>
                        ) : (
                          <><CheckCircle2 className="w-4 h-4" /> Save Family Members</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {onboardingComplete && (
            <div className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white shadow-lg animate-in fade-in slide-in-from-bottom-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-bold">Onboarding Complete</h4>
                  <p className="text-emerald-50 text-sm mt-1">
                    Your welfare profile is fully set up. You and your registered family members are now covered under the group's policy.
                  </p>
                  {welfareMemberIdentifier ? (
                    <p className="text-emerald-50 text-sm mt-2">
                      Your Welfare Member ID: <span className="font-semibold">{welfareMemberIdentifier}</span>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GroupWelfareOnboardingModal
